/*
 * Grabado personalizado — Mundo Patagón
 * -------------------------------------
 * Tres opciones, ninguna preseleccionada:
 *   - No personalizar  -> sin cargo
 *   - Grabar texto     -> cargo según el tag del producto (gratis / 5000 / 9900)
 *   - Grabar imagen    -> siempre +$9.900
 * Texto e imagen son excluyentes.
 *
 * - Bloquea el botón "Comprar" hasta que el cliente elige una opción. Volver a
 *   tocar la opción ya elegida la desmarca, y el botón se bloquea otra vez.
 * - Valida el texto (no vacío, y dentro del largo que permita el producto: 2
 *   líneas y 40 caracteres, o 1 y 20 en los cortaplumas) y la imagen (archivo
 *   obligatorio), con aviso en vivo. Los topes los inyecta el tema.
 * - Preview del logo en escala de grises (solo visual; al pedido llega el original).
 * - Cuando la opción tiene cargo, agrega al carro el producto de cobro. Los IDs
 *   de variante los inyecta el tema (resueltos por handle en
 *   engraving-options.liquid), así no hay que copiar IDs a mano.
 * - Con cantidad mayor a 1 se pide un grabado POR CUCHILLO. Ver más abajo.
 *
 * ---------------------------------------------------------------------------
 * Arreglos sobre pedidos que llegaban mal al taller
 *
 * 2026-08-27 · Tres cuchillos salían del taller con el mismo grabado.
 *   Con el descuento por volumen de Vitals, llevar 2 o 3 pasó a ser el camino
 *   que la tienda empuja, y el grabado se pedía una sola vez por compra: quien
 *   compraba tres de regalo escribía un texto y los tres salían iguales. Ahora
 *   se muestra un juego de campos por unidad.
 *
 *   Dos cosas hacían falta para eso, y ninguna es cosmética:
 *
 *   a) Saber la cantidad. Se leía con this.form.querySelector('[name=quantity]'),
 *      pero ese input vive FUERA del formulario y se asocia por el atributo
 *      form= (sections/main-product.liquid). El querySelector no lo encontraba
 *      nunca, así que qty era SIEMPRE 1 y la línea de cobro entraba con cantidad
 *      1 aunque se compraran tres. No se notaba porque cart-engraving-sync.js la
 *      corregía después, en el carro. Se usa form.elements.quantity, que sí
 *      incluye los elementos asociados por atributo.
 *
 *   b) Partir la compra. Shopify guarda las propiedades POR LÍNEA, no por
 *      unidad: una línea de cantidad 3 tiene un solo "Texto a grabar". Para que
 *      cada cuchillo lleve el suyo hay que mandar 3 líneas de cantidad 1. Se
 *      parte SOLO cuando los grabados realmente difieren; si son iguales sale
 *      una línea de cantidad 3, igual que siempre.
 *
 *   El cobro es uno por pieza grabada: tres hojas grabadas son tres trabajos.
 *   Es lo que la tienda ya cobraba, solo que ahora la cuenta entra bien desde el
 *   cart/add.js en vez de depender de que el carro la arregle.
 *
 * 2026-08-03 · Pedidos intermitentes sin el texto a grabar.
 *   El tema registra su handler de submit en el constructor de <product-form>,
 *   y product-form.js se carga mucho antes que este archivo. Como en el
 *   elemento destino los listeners corren en orden de registro, el tema
 *   serializaba el formulario y hacía su POST antes de que este código mirara
 *   nada. Quedaban dos peticiones compitiendo contra el redirect al carro y el
 *   resultado dependía del azar. Por eso ahora escuchamos en el document en
 *   fase de captura: es lo único que garantiza llegar primero.
 *   NO volver a mover este listener al formulario.
 *
 * 2026-07-31 · Pedidos con el cargo aplicado pero sin texto.
 *   El campo solo se habilitaba al elegir su tarjeta, y connectedCallback lo
 *   vuelve a deshabilitar en cualquier reinserción del DOM; deshabilitado, no
 *   entra al FormData. Se reafirma el estado en onSubmit y las propiedades de
 *   texto se escriben explícitamente.
 *
 * 2026-07-31 · La imagen no se podía adjuntar desde el celular.
 *   El accept cerrado dejaba fuera las fotos HEIC, y un archivo que el
 *   navegador no sabe dibujar no daba ninguna señal. Ver engraving-options.liquid.
 * ---------------------------------------------------------------------------
 */

// Tope de juegos de campos en pantalla. Por encima no se dibuja uno por unidad:
// un pedido de 50 cuchillos con 50 formularios no es usable, y ese caso es
// siempre el mismo grabado repetido (el logo de una empresa).
const ENGRAVING_PIECES_MAX = 20;

class EngravingOptions extends HTMLElement {
  constructor() {
    super();
    this.tier = parseInt(this.dataset.tier, 10) || 0;
    this.textVariantId = this.dataset.textVariant || '';
    this.imageVariantId = this.dataset.imageVariant || '';
    this.productTitle = this.dataset.productTitle || '';
    // Cuánto texto entra en la hoja. Lo decide el tema según el tag del
    // producto; acá solo se respeta, con el tope común como respaldo.
    this.maxLines = parseInt(this.dataset.maxLines, 10) || 2;
    this.maxChars = parseInt(this.dataset.maxChars, 10) || 40;
    this.form = this.closest('form');
    this.submitButton = document.getElementById(this.dataset.submitButton);

    this.radios = this.querySelectorAll('.engraving__radio');
    this.textFields = this.querySelector('.engraving__fields--text');
    this.imageFields = this.querySelector('.engraving__fields--image');
    this.errorEl = this.querySelector('.engraving__error');
    this.requiredEl = this.querySelector('.engraving__required');

    // Un juego de campos por cuchillo. La pieza 1 viene del Liquid y conserva su
    // name; las demás las clona el JS del <template> y no llevan name, porque en
    // el camino partido cada línea se arma a mano.
    this.textPieces = this.querySelector('.engraving__pieces--text');
    this.imagePieces = this.querySelector('.engraving__pieces--image');
    this.textTemplate = this.querySelector('.engraving__piece-template--text');
    this.imageTemplate = this.querySelector('.engraving__piece-template--image');
    this.sameWrap = this.querySelector('.engraving__same');
    this.sameInput = this.querySelector('.engraving__same-input');
    this.noteEl = this.querySelector('.engraving__pieces-note');
    this.pieceCount = 1;

    // Si el botón ya venía deshabilitado (agotado), no lo tocamos.
    this.soldOut = this.submitButton ? this.submitButton.disabled : false;
  }

  connectedCallback() {
    if (!this.form || !this.submitButton) return;

    if (!this.soldOut) this.setSubmitEnabled(false);

    // connectedCallback corre en CADA inserción en el DOM, y los elementos hijos
    // se mueven con el componente conservando sus listeners: volver a cablearlos
    // los duplicaría, y con ellos el contador de caracteres. Por eso el cableado
    // de los hijos va una sola vez.
    //
    // Lo que NO puede ir acá dentro es lo que disconnectedCallback deshace: el
    // handler de submit del document y el temporizador de la cantidad. Esos se
    // rearman en cada inserción, más abajo.
    if (!this.wired) {
      this.wired = true;
      this.wireChildren();
    }

    // Campos deshabilitados de inicio: así no viajan propiedades vacías
    // en pedidos sin grabado (los inputs disabled no entran al FormData).
    this.setFieldsEnabled(null);
    this.startQuantityTimer();
    this.syncPieces();

    // Interceptamos el submit ANTES que el tema. No basta con la fase de
    // captura sobre el formulario: en el elemento destino los listeners corren
    // en orden de registro, y el del tema se registra primero (product-form.js
    // se carga en la línea 43 de main-product.liquid; este script viaja dentro
    // del bloque buy_buttons, mucho más abajo).
    //
    // Escuchando en el documento sí ganamos: la captura baja desde arriba y
    // llega acá antes de tocar el formulario. Así, cuando hay que bloquear o
    // tomar el control, stopPropagation() evita que el tema llegue a serializar
    // y agregar el producto por su cuenta.
    this.submitHandler = (e) => {
      if (e.target !== this.form) return;
      this.onSubmit(e);
    };
    document.addEventListener('submit', this.submitHandler, true);
  }

  // Cableado de los hijos: una sola vez por elemento.
  wireChildren() {
    // Un grupo de radios no se puede vaciar por sí solo: una vez elegido uno,
    // siempre queda algo marcado. Para poder volver a "sin elegir" —y que el
    // botón se bloquee de nuevo— hay que mirar el estado ANTES de que el
    // navegador lo cambie: cuando llega el click el radio ya figura marcado y
    // no habría con qué comparar.
    //
    // El listener va en la TARJETA, no en el radio. La tarjeta es un <label>
    // que envuelve al input, así que al tocar el texto el navegador fabrica un
    // click sobre el radio sin que este reciba ningún pointerdown propio; si
    // escucháramos solo en el input, el estado quedaría viejo y la tarjeta se
    // desmarcaría sola en el clic siguiente.
    this.radios.forEach((r) => {
      const card = r.closest('.engraving__card') || r;
      const recordar = () => {
        this.radioYaEstaba = r.checked;
      };

      card.addEventListener('pointerdown', recordar);
      // Con teclado el espacio es lo que activa el radio; las flechas mueven la
      // selección y ahí desmarcar sería confuso, así que no se tocan.
      r.addEventListener('keydown', (e) => {
        if (e.key === ' ') recordar();
      });

      r.addEventListener('click', () => {
        if (!this.radioYaEstaba) return;
        r.checked = false;
        this.radioYaEstaba = false;
        // Desmarcar por código no dispara 'change', hay que avisar a mano.
        this.onChoiceChange();
      });

      r.addEventListener('change', () => this.onChoiceChange());
    });

    // Delegación en el contenedor, no un listener por campo: las piezas 2..N
    // aparecen y desaparecen cuando cambia la cantidad, y así no hay que
    // cablearlas ni descablearlas cada vez.
    this.wirePieces(this.textPieces);
    this.wirePieces(this.imagePieces);

    if (this.sameInput) {
      this.sameInput.addEventListener('change', () => this.refreshPieces());
    }

    this.wireQuantity();
  }

  disconnectedCallback() {
    // Sin esto, reinsertar el elemento dejaría listeners duplicados en el
    // documento y el cobro se agregaría dos veces.
    if (this.submitHandler) {
      document.removeEventListener('submit', this.submitHandler, true);
    }
    if (this.qtyTimer) clearInterval(this.qtyTimer);
  }

  // 'none' | 'text' | 'image' | null (sin elegir)
  get choice() {
    const checked = this.querySelector('.engraving__radio:checked');
    return checked ? checked.dataset.choice : null;
  }

  // Cargo que corresponde a la opción elegida.
  get chargeVariantId() {
    if (this.choice === 'image') return this.imageVariantId;
    if (this.choice === 'text' && this.tier > 0) return this.textVariantId;
    return '';
  }

  // ---------------------------------------------------------------------------
  // Cantidad
  // ---------------------------------------------------------------------------

  // El selector de cantidad está FUERA del formulario y se asocia por el
  // atributo form=. form.querySelector no lo encuentra nunca; form.elements sí
  // incluye los elementos asociados por atributo. Ver el arreglo de 2026-08-27.
  get quantityInput() {
    if (!this.form) return null;
    const el = this.form.elements.quantity;
    if (!el) return null;
    return el.tagName ? el : el[0] || null;
  }

  get quantity() {
    const el = this.quantityInput;
    const n = el ? parseInt(el.value, 10) : 1;
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  // Cuántos juegos de campos mostrar. Pasado el tope se vuelve a uno solo y se
  // graba lo mismo en todas las unidades.
  get pieceTarget() {
    const n = this.quantity;
    return n > ENGRAVING_PIECES_MAX ? 1 : n;
  }

  get sameForAll() {
    return this.sameInput ? this.sameInput.checked : false;
  }

  // Solo con varias piezas independientes tiene sentido dejar una en blanco:
  // con una sola, para no grabar está la tarjeta "No personalizar".
  get piezasIndependientes() {
    return this.pieceCount > 1 && !this.sameForAll;
  }

  wireQuantity() {
    const el = this.quantityInput;
    if (!el) return;
    el.addEventListener('change', () => this.syncPieces());
    el.addEventListener('input', () => this.syncPieces());
  }

  startQuantityTimer() {
    // Vitals escribe la cantidad por código al elegir un tramo del descuento por
    // volumen ("Lleva 3"), y asignar .value NO dispara ningún evento. Sin esto
    // los campos se quedarían en uno solo aunque el carro lleve tres. Leer el
    // .value de un input cada medio segundo no cuesta nada, y cubre a cualquier
    // app que haga lo mismo, dispare evento o no.
    if (this.qtyTimer) clearInterval(this.qtyTimer);
    this.qtyTimer = setInterval(() => {
      if (this.pieceTarget !== this.pieceCount) this.syncPieces();
    }, 500);
  }

  // ---------------------------------------------------------------------------
  // Piezas
  // ---------------------------------------------------------------------------

  containerFor(choice) {
    return choice === 'image' ? this.imagePieces : this.textPieces;
  }

  allPieces(choice) {
    const c = this.containerFor(choice);
    return c ? Array.from(c.querySelectorAll('.engraving__piece')) : [];
  }

  // Las piezas que realmente se piden: una sola si está marcado "el mismo en
  // todos", y ninguna si la opción elegida no lleva campos.
  activePieces(choice) {
    if (choice !== 'text' && choice !== 'image') return [];
    const all = this.allPieces(choice);
    return this.sameForAll ? all.slice(0, 1) : all;
  }

  pieceTextInput(piece) {
    return piece.querySelector('.engraving__text');
  }

  pieceFileInput(piece) {
    return piece.querySelector('.engraving__file');
  }

  // Vale cero si la pieza está marcada como sin grabado, aunque el cliente haya
  // escrito algo antes de marcarla. Con esto el resto del código —qué se parte,
  // qué propiedades van y qué se cobra— no necesita saber de la casilla.
  pieceText(piece) {
    if (this.pieceSinGrabado(piece)) return '';
    const i = this.pieceTextInput(piece);
    return i ? i.value.trim() : '';
  }

  // "Este cuchillo sin grabado": para llevar varios y grabar solo algunos. Con un
  // solo cuchillo no aplica —para no grabar está la tarjeta "No personalizar"—,
  // así que la casilla ni se muestra y su estado no se mira.
  pieceSkipInput(piece) {
    return piece.querySelector('.engraving__piece-skip-input');
  }

  pieceSinGrabado(piece) {
    if (!this.piezasIndependientes) return false;
    const cb = this.pieceSkipInput(piece);
    return !!(cb && cb.checked);
  }

  pieceFile(piece) {
    const i = this.pieceFileInput(piece);
    return i && i.files && i.files[0] ? i.files[0] : null;
  }

  // Atajos a la pieza 1, que es la única que lleva name y por lo tanto la única
  // que importa cuando el formulario se serializa solo (grabado sin cargo).
  get textInput() {
    const p = this.allPieces('text')[0];
    return p ? this.pieceTextInput(p) : null;
  }

  get imageTextInput() {
    const p = this.allPieces('image')[0];
    return p ? this.pieceTextInput(p) : null;
  }

  get fileInput() {
    const p = this.allPieces('image')[0];
    return p ? this.pieceFileInput(p) : null;
  }

  wirePieces(container) {
    if (!container) return;

    container.addEventListener('input', (e) => {
      if (e.target.classList.contains('engraving__text')) this.onPieceInput(e.target);
    });

    container.addEventListener('change', (e) => {
      if (e.target.classList.contains('engraving__file')) this.onPieceFile(e.target);
      if (e.target.classList.contains('engraving__piece-skip-input')) this.onPieceSkip(e.target);
    });

    // Cortamos el Enter que crearía una línea de más, en vez de dejar que la
    // escriba y recién ahí rechazarla.
    container.addEventListener('keydown', (e) => {
      if (!e.target.classList.contains('engraving__text')) return;
      if (e.key === 'Enter' && e.target.value.split('\n').length >= this.maxLines) {
        e.preventDefault();
      }
    });
  }

  // Clona o descarta piezas hasta que haya tantas como unidades. La primera
  // nunca se saca: es la del Liquid.
  buildPieces(choice) {
    const container = this.containerFor(choice);
    const template = choice === 'image' ? this.imageTemplate : this.textTemplate;
    if (!container || !template) return;

    const n = this.pieceCount;
    let pieces = container.querySelectorAll('.engraving__piece');

    for (let i = pieces.length; i < n; i++) {
      // __N__ es lo único que hay que sustituir: mantiene únicos los id y los
      // for= de las etiquetas. El resto del molde ya viene armado del Liquid,
      // con los topes de líneas y caracteres del producto.
      const holder = document.createElement('div');
      holder.innerHTML = template.innerHTML.replace(/__N__/g, String(i + 1));
      const el = holder.querySelector('.engraving__piece');
      if (el) container.appendChild(el);
    }

    pieces = container.querySelectorAll('.engraving__piece');
    for (let i = pieces.length - 1; i >= Math.max(n, 1); i--) pieces[i].remove();
  }

  syncPieces() {
    const forzado = this.quantity > ENGRAVING_PIECES_MAX;

    if (this.sameInput) {
      if (forzado && !this.sameInput.checked) this.sameInput.checked = true;
      this.sameInput.disabled = forzado;
    }
    if (this.noteEl) {
      this.noteEl.hidden = !forzado;
      if (forzado) {
        this.noteEl.textContent = `Para más de ${ENGRAVING_PIECES_MAX} unidades se graba lo mismo en todas.`;
      }
    }

    const n = this.pieceTarget;
    if (n !== this.pieceCount) {
      this.pieceCount = n;
      this.buildPieces('text');
      this.buildPieces('image');
    }

    this.refreshPieces();
  }

  // Visibilidad, numeración y habilitados. Se llama cada vez que cambia algo que
  // los afecte: la cantidad, la casilla "el mismo en todos" o la tarjeta elegida.
  refreshPieces() {
    const same = this.sameForAll;
    const n = this.pieceCount;
    const numerar = n > 1 && !same;

    ['text', 'image'].forEach((choice) => {
      this.allPieces(choice).forEach((piece, i) => {
        piece.hidden = same && i > 0;
        piece.querySelectorAll('.engraving__piece-num').forEach((span) => {
          span.hidden = !numerar;
          span.textContent = numerar ? ` — cuchillo ${i + 1} de ${n}` : '';
        });
        // La casilla solo tiene sentido con varias piezas independientes. Si se
        // vuelve a una sola se esconde Y se desmarca: dejarla marcada de forma
        // invisible haría que el cuchillo saliera sin grabar sin que se vea.
        const skip = piece.querySelector('.engraving__piece-skip');
        const skipInput = this.pieceSkipInput(piece);
        if (skip) skip.hidden = !numerar;
        if (skipInput && !numerar && skipInput.checked) skipInput.checked = false;
        piece.classList.toggle(
          'engraving__piece--skipped',
          !!(numerar && skipInput && skipInput.checked)
        );
      });
    });

    const choice = this.choice;
    if (this.sameWrap) {
      this.sameWrap.hidden = n < 2 || (choice !== 'text' && choice !== 'image');
    }
    this.setFieldsEnabled(choice);
  }

  onChoiceChange() {
    const choice = this.choice;
    if (this.textFields) this.textFields.hidden = choice !== 'text';
    if (this.imageFields) this.imageFields.hidden = choice !== 'image';
    if (this.errorEl) this.errorEl.hidden = true;
    this.refreshPieces();
    if (!this.soldOut) this.setSubmitEnabled(choice !== null);
  }

  // Solo habilita los campos de la opción elegida; los demás quedan fuera
  // del FormData para que no lleguen propiedades vacías al pedido. Las piezas
  // escondidas también se apagan, para que no se pueda entrar con el tabulador.
  setFieldsEnabled(choice) {
    this.allPieces('text').forEach((piece, i) => {
      const t = this.pieceTextInput(piece);
      if (t) {
        t.disabled =
          choice !== 'text' || (this.sameForAll && i > 0) || this.pieceSinGrabado(piece);
      }
    });

    // El texto opcional de la imagen se habilita con su tarjeta. Deshabilitado
    // no entra al FormData, que es justo lo que queremos si graba solo texto:
    // los dos campos comparten el nombre de propiedad y se pisarían.
    this.allPieces('image').forEach((piece, i) => {
      const apagada = choice !== 'image' || (this.sameForAll && i > 0);
      const f = this.pieceFileInput(piece);
      const t = this.pieceTextInput(piece);
      if (f) f.disabled = apagada;
      if (t) t.disabled = apagada;
    });
  }

  setSubmitEnabled(enabled) {
    this.submitButton.disabled = !enabled;

    // Un botón deshabilitado no recibe clics, así que no hay ningún gesto con
    // el que disparar la explicación: tiene que estar a la vista mientras dura
    // el bloqueo. Aparece con el bloqueo y se retira al elegir una tarjeta.
    // Solo se llega acá cuando el producto NO está agotado (ver this.soldOut),
    // así que el cartel nunca contradice a un botón bloqueado por stock.
    if (this.requiredEl) {
      this.requiredEl.hidden = enabled;

      // Que el lector de pantalla lea el motivo al llegar al botón, no solo
      // cuando el cartel aparece.
      if (this.requiredEl.id) {
        if (enabled) this.submitButton.removeAttribute('aria-describedby');
        else this.submitButton.setAttribute('aria-describedby', this.requiredEl.id);
      }
    }
  }

  showError(msg) {
    if (!this.errorEl) return;
    if (msg) this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
  }

  // Con un solo campo el mensaje se explica solo; con tres, un error sin número
  // obliga al cliente a adivinar cuál de los tres revisar. Al anteponer la pieza
  // el mensaje deja de empezar la frase, así que se le baja la inicial.
  conPieza(i, total, msg) {
    if (total < 2) return msg;
    return `Cuchillo ${i + 1}: ${msg.charAt(0).toLowerCase()}${msg.slice(1)}`;
  }

  // Devuelve un mensaje de error si el texto no cumple las reglas, o null si va
  // bien. Los topes salen del producto (ver maxLines/maxChars); el maxlength del
  // campo frena el tecleo, pero pegar texto de más sí llega hasta acá.
  // (El vacío se maneja aparte.)
  textError(value) {
    const t = (value || '').trim();
    if (t.length === 0) return null;
    if (t.split('\n').length > this.maxLines) {
      return this.maxLines === 1
        ? 'El texto debe caber en una sola línea.'
        : `El texto no puede tener más de ${this.maxLines} líneas.`;
    }
    if (t.length > this.maxChars) {
      return `El texto no puede superar los ${this.maxChars} caracteres.`;
    }
    return null;
  }

  onPieceInput(input) {
    const piece = input.closest('.engraving__piece');
    const counter = piece && piece.querySelector('[data-count], [data-image-count]');
    if (counter) counter.textContent = input.value.length;

    const err = this.textError(input.value);
    if (err) {
      this.showError(err);
      if (!this.soldOut) this.setSubmitEnabled(false);
    } else {
      if (this.errorEl) this.errorEl.hidden = true;
      if (!this.soldOut) this.setSubmitEnabled(this.choice !== null);
    }
  }

  onPieceSkip(input) {
    const piece = input.closest('.engraving__piece');
    if (!piece) return;

    // El campo se apaga y se ve apagado: es lo que hace que el estado se lea de
    // un vistazo, en vez de depender de recordar que la casilla está marcada.
    piece.classList.toggle('engraving__piece--skipped', input.checked);
    this.setFieldsEnabled(this.choice);

    // Un texto de más ya no bloquea si la pieza quedó fuera, y al revés: volver
    // a incluirla puede destrabar el botón.
    if (this.errorEl) this.errorEl.hidden = true;
    if (!this.soldOut) this.setSubmitEnabled(this.choice !== null);
  }

  onPieceFile(input) {
    if (this.errorEl) this.errorEl.hidden = true;
    const piece = input.closest('.engraving__piece');
    if (!piece) return;

    const file = input.files && input.files[0];
    const nombreEl = piece.querySelector('.engraving__filename');

    // Confirmación en texto ANTES de intentar dibujar: el cliente tiene que
    // saber que el archivo entró aunque el navegador no sepa mostrarlo. En el
    // celular esto es lo único visible con fotos HEIC del iPhone.
    if (nombreEl) {
      if (file) {
        const mb = file.size / (1024 * 1024);
        const peso = mb < 0.1 ? '' : ` · ${mb.toFixed(1)} MB`;
        nombreEl.textContent = `Archivo adjunto: ${file.name}${peso}`;
        nombreEl.hidden = false;
      } else {
        nombreEl.hidden = true;
      }
    }

    this.renderGrayscalePreview(piece);
  }

  renderGrayscalePreview(piece) {
    const wrap = piece.querySelector('.engraving__preview');
    const canvas = piece.querySelector('.engraving__canvas');
    const file = this.pieceFile(piece);
    if (!file || !canvas) {
      if (wrap) wrap.hidden = true;
      return;
    }

    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const ctx = canvas.getContext('2d');
      const size = canvas.width;
      ctx.clearRect(0, 0, size, size);
      // Encajar la imagen dentro del canvas manteniendo proporción.
      const scale = Math.min(size / img.width, size / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      ctx.filter = 'grayscale(1) contrast(1.1)';
      ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
      if (wrap) wrap.hidden = false;
      URL.revokeObjectURL(url);
    };
    // Formatos que el navegador no sabe dibujar (HEIC, por ejemplo) no son un
    // error: el archivo se envía igual. Solo ocultamos la vista previa y
    // avisamos, en vez de dejar al cliente sin ninguna señal.
    img.onerror = () => {
      if (wrap) wrap.hidden = true;
      const nombreEl = piece.querySelector('.engraving__filename');
      if (nombreEl) {
        nombreEl.textContent += ' — no se puede previsualizar aquí, pero se enviará igual.';
      }
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }

  // Corta el envío de raíz. stopPropagation() es lo que impide que el evento
  // siga bajando hasta el formulario, donde el tema lo agregaría al carro.
  block(e, msg) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    this.showError(msg);
  }

  onSubmit(e) {
    // Releer la cantidad justo antes de enviar. Es la red de seguridad: si algo
    // la cambió sin disparar evento y el intervalo todavía no pasó, acá se
    // corrige, y no sale un pedido con menos textos que cuchillos.
    this.syncPieces();

    const choice = this.choice;

    // 0) Garantía: los campos de la opción elegida DEBEN estar habilitados al
    //    serializar, o su valor no viaja y el pedido llega sin el dato. No
    //    basta con habilitarlos al elegir la tarjeta: si algo reinserta este
    //    elemento en el DOM, connectedCallback vuelve a deshabilitarlos. Lo
    //    reafirmamos aquí, que es el único momento que importa.
    this.setFieldsEnabled(choice);

    // 1) Sin elección => bloquear.
    if (choice === null) {
      this.block(e, 'Elige una opción de personalización para continuar.');
      return;
    }

    const pieces = this.activePieces(choice);
    const total = pieces.length;

    // 2) Grabado de texto => texto obligatorio y dentro de las reglas, en CADA
    //    pieza. Normalizamos antes de enviar para que al pedido no lleguen
    //    espacios sobrantes; vale para los dos caminos, el envío nativo del tema
    //    y el que agrega el producto de cobro.
    if (choice === 'text') {
      let conGrabado = 0;
      for (let i = 0; i < total; i++) {
        const input = this.pieceTextInput(pieces[i]);
        const value = input ? input.value : '';

        // Pieza marcada como sin grabado: no se valida ni se cuenta.
        if (this.pieceSinGrabado(pieces[i])) continue;

        if (value.trim().length === 0) {
          this.block(
            e,
            total > 1
              ? `Cuchillo ${i + 1}: escribe el texto a grabar, o marca "Este cuchillo sin grabado".`
              : 'Escribe el texto a grabar.'
          );
          if (input) input.focus();
          return;
        }

        const err = this.textError(value);
        if (err) {
          this.block(e, this.conPieza(i, total, err));
          if (input) input.focus();
          return;
        }
        input.value = value.trim();
        conGrabado += 1;
      }

      // Todos en blanco es un pedido sin ningún grabado, que ya tiene su tarjeta.
      // Dejarlo pasar cobraría cero y agregaría la propiedad a ninguna línea.
      if (conGrabado === 0) {
        this.block(e, 'No hay ningún cuchillo con texto. Si no quieres grabar ninguno, elige "No personalizar".');
        return;
      }
    }

    // 3) Grabado de imagen => archivo obligatorio en cada pieza; el texto que lo
    //    acompaña es opcional, pero si viene escrito tiene que cumplir las
    //    mismas reglas.
    if (choice === 'image') {
      for (let i = 0; i < total; i++) {
        const fileInput = this.pieceFileInput(pieces[i]);
        if (!fileInput || fileInput.files.length === 0) {
          this.block(e, this.conPieza(i, total, 'Sube la imagen o logo que quieres grabar.'));
          return;
        }
        const textInput = this.pieceTextInput(pieces[i]);
        if (textInput) {
          const err = this.textError(textInput.value);
          if (err) {
            this.block(e, this.conPieza(i, total, err));
            if (textInput) textInput.focus();
            return;
          }
          textInput.value = textInput.value.trim();
        }
      }

      // 3.b) Texto opcional vacío => lo deshabilitamos para que no viaje como
      //      propiedad en blanco. Hace falta acá y no solo al armar el carro,
      //      porque el camino sin cargo (tag `grabado-imagen-gratis`) lo
      //      serializa el tema y ahí no tocamos el FormData. Un input
      //      deshabilitado no entra.
      const primero = this.imageTextInput;
      if (primero && primero.value.trim() === '') primero.disabled = true;
    }

    const partir = this.needsSplit(choice, pieces);
    const variantId = this.chargeVariantId;

    // 4) Sin partir y sin cargo (no personalizar, o grabado gratis con un solo
    //    texto) => flujo normal del tema, exactamente igual que siempre.
    if (!partir && !variantId) return;

    // 5) Tomamos el control. Sin stopPropagation el tema también agregaría el
    //    producto, y con dos peticiones compitiendo contra el redirect al carro
    //    el resultado quedaba a suerte.
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    this.addToCart(variantId, choice, pieces, partir);
  }

  // Solo se parte el carro cuando los grabados realmente difieren. Si el cliente
  // escribió lo mismo en los tres campos sale una línea de cantidad 3, como
  // siempre: el carro queda corto y no hay peticiones de más. Con la casilla "el
  // mismo en todos" marcada hay una sola pieza y nunca se parte.
  needsSplit(choice, pieces) {
    if (pieces.length < 2) return false;
    if (choice === 'text') {
      const primero = this.pieceText(pieces[0]);
      return pieces.some((p) => this.pieceText(p) !== primero);
    }
    if (choice === 'image') {
      const archivo = this.pieceFile(pieces[0]);
      const texto = this.pieceText(pieces[0]);
      return pieces.some((p) => this.pieceFile(p) !== archivo || this.pieceText(p) !== texto);
    }
    return false;
  }

  // Etiqueta compartida que enlaza el cuchillo con su grabado en el carro.
  //
  // Antes era solo un token al azar, y en el admin la línea de cobro
  // ("Grabado $9.900") no mostraba más que eso: ruido ilegible para el taller.
  // Ahora la etiqueta lleva delante el nombre del cuchillo, así esa línea se
  // explica sola. La cola al azar se queda porque es lo que hace única a cada
  // pieza: sin ella, dos cuchillos iguales en el mismo carro compartirían
  // etiqueta y la sincronización los confundiría.
  //
  // El guion bajo del nombre de la propiedad es lo que la mantiene fuera del
  // checkout y del correo del cliente; en el admin sí se ve.
  nuevaEtiqueta() {
    const token = 'g' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
    return this.productTitle ? `${this.productTitle} · ${token}` : token;
  }

  get addUrl() {
    return `${window.Shopify.routes.root}cart/add.js`;
  }

  async addToCart(variantId, choice, pieces, partir) {
    this.setSubmitEnabled(false);
    this.submitButton.classList.add('loading');

    try {
      if (partir) await this.addPartido(variantId, choice, pieces);
      else await this.addEntero(variantId);
      window.location = `${window.Shopify.routes.root}cart`;
    } catch (err) {
      console.error('[grabado] Error al agregar al carro', err);
      this.showError('Hubo un problema al agregar el grabado. Intenta de nuevo.');
      this.setSubmitEnabled(true);
      this.submitButton.classList.remove('loading');
    }
  }

  // Una línea de cuchillo y una de cobro, ambas con la cantidad del selector.
  // Es el camino de siempre.
  async addEntero(variantId) {
    const etiqueta = this.nuevaEtiqueta();

    // a) Agregar el cuchillo con sus propiedades (incluye archivo si hay) + token.
    //    Las propiedades de texto se reescriben explícitamente en vez de
    //    confiar en lo que haya serializado el formulario: es la diferencia
    //    entre un pedido con el grabado y uno que llega vacío al taller.
    const knifeData = new FormData(this.form);
    const checked = this.querySelector('.engraving__radio:checked');
    if (checked) knifeData.set('properties[Personalización]', checked.value);
    if (this.choice === 'text' && this.textInput) {
      knifeData.set('properties[Texto a grabar]', this.textInput.value.trim());
    }
    // El texto de la imagen es opcional: si quedó vacío se saca la propiedad
    // en vez de mandarla en blanco, para que el pedido no llegue con un campo
    // "Texto a grabar" vacío que el taller tenga que interpretar.
    if (this.choice === 'image' && this.imageTextInput) {
      const extra = this.imageTextInput.value.trim();
      if (extra) knifeData.set('properties[Texto a grabar]', extra);
      else knifeData.delete('properties[Texto a grabar]');
    }
    knifeData.append('properties[_Grabado para]', etiqueta);

    const knifeRes = await fetch(this.addUrl, { method: 'POST', body: knifeData });

    // Si el cuchillo no entró, no agregamos el cobro: cobrar un grabado sin
    // el producto es peor que fallar a la vista del cliente.
    if (!knifeRes.ok) {
      const detalle = await knifeRes.json().catch(() => ({}));
      throw new Error(detalle.description || 'No se pudo agregar el producto');
    }

    // b) Agregar el servicio de grabado, misma cantidad y misma etiqueta.
    //    Solo lleva la etiqueta (oculta al cliente): el detalle de qué grabar
    //    ya viaja en la línea del cuchillo, y repetirlo lo duplicaba en el
    //    correo. La etiqueta además es distinta en cada compra, así que dos
    //    líneas de cobro nunca se fusionan entre sí.
    //
    //    La cantidad sale de form.elements.quantity. Antes salía siempre 1 por
    //    el bug del selector, y era cart-engraving-sync.js el que la corregía al
    //    llegar al carro; ahora entra bien de entrada y el sync pasa a ser red
    //    de seguridad en vez de ser el único que hace la cuenta.
    await this.agregarCobros(variantId, [etiqueta], this.quantity);
  }

  // Una línea de cantidad 1 por cuchillo, cada una con su grabado y su etiqueta,
  // más una línea de cobro por pieza. Dos líneas con propiedades distintas no se
  // fusionan, así que la separación se sostiene sola.
  async addPartido(variantId, choice, pieces) {
    const n = pieces.length;
    const etiquetas = pieces.map(() => this.nuevaEtiqueta());
    const checked = this.querySelector('.engraving__radio:checked');
    const base = this.itemBase();

    // El rótulo de la tarjeta "No personalizar", para no escribirlo a mano acá.
    const sinGrabado = this.querySelector('.engraving__radio[data-choice="none"]');

    const propiedadesDe = (i) => {
      const props = Object.assign({}, base.properties);
      if (checked) props['Personalización'] = checked.value;
      // Para que el taller lea el orden sin tener que deducirlo.
      props['Pieza'] = `${i + 1} de ${n}`;
      props['_Grabado para'] = etiquetas[i];
      const texto = this.pieceText(pieces[i]);
      if (texto) {
        props['Texto a grabar'] = texto;
      } else {
        // Pieza dejada en blanco a propósito: este cuchillo sale sin grabar. La
        // línea tiene que decirlo, o el taller leería "Grabado de texto" sin
        // texto y no sabría si es un error o una intención.
        delete props['Texto a grabar'];
        if (sinGrabado) props['Personalización'] = sinGrabado.value;
      }
      return props;
    };

    if (choice === 'image') {
      // El archivo obliga a multipart, y multipart admite un ítem por petición:
      // los cuchillos van de a uno.
      for (let i = 0; i < n; i++) {
        const fd = new FormData(this.form);
        fd.set('quantity', '1');
        // Lo que trae el formulario son las propiedades de la pieza 1; se
        // reescriben con las de esta pieza. El texto se borra antes porque
        // puede no corresponder ninguno.
        fd.delete('properties[Texto a grabar]');
        const props = propiedadesDe(i);
        Object.keys(props).forEach((k) => fd.set(`properties[${k}]`, props[k]));
        const archivo = this.pieceFile(pieces[i]);
        if (archivo) fd.set('properties[Logo]', archivo, archivo.name);

        const res = await fetch(this.addUrl, { method: 'POST', body: fd });
        if (!res.ok) {
          const detalle = await res.json().catch(() => ({}));
          throw new Error(detalle.description || 'No se pudo agregar el producto');
        }
      }
    } else {
      // Sin archivos entran todas en una sola petición JSON: rápido y atómico.
      const items = pieces.map((_, i) =>
        Object.assign({}, base, { quantity: 1, properties: propiedadesDe(i) })
      );
      const res = await fetch(this.addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const detalle = await res.json().catch(() => ({}));
        throw new Error(detalle.description || 'No se pudo agregar el producto');
      }
    }

    // Un cobro por pieza GRABADA: tres hojas grabadas son tres trabajos, pero el
    // cuchillo que se dejó en blanco no se cobra. Con grabado gratis no hay
    // variante y no se cobra nada, pero el carro se parte igual para que cada
    // cuchillo lleve su texto.
    const cobrables = etiquetas.filter((_, i) => this.pieceText(pieces[i]) !== '');
    await this.agregarCobros(variantId, cobrables, 1);
  }

  async agregarCobros(variantId, etiquetas, cantidad) {
    if (!variantId || etiquetas.length === 0) return;
    await fetch(this.addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: etiquetas.map((etiqueta) => ({
          id: Number(variantId),
          quantity: cantidad,
          properties: { '_Grabado para': etiqueta },
        })),
      }),
    });
  }

  // El ítem tal como lo ve el formulario: la variante y las propiedades que
  // puedan haber puesto otras apps. Se arma leyendo el FormData en vez de a mano
  // para no perderlas al partir la compra en varias líneas.
  itemBase() {
    const item = { id: 0, properties: {} };
    new FormData(this.form).forEach((value, key) => {
      if (typeof value !== 'string') return; // los archivos no viajan por JSON
      const m = key.match(/^properties\[(.+)\]$/);
      if (m) {
        if (value !== '') item.properties[m[1]] = value;
        return;
      }
      if (key === 'id') item.id = Number(value);
      else if (key === 'selling_plan' && value) item.selling_plan = value;
    });
    return item;
  }
}

customElements.define('engraving-options', EngravingOptions);
